import { useMessage } from '@/common/inject/useMessage';
import { useInternational } from '@/common/locale';
import { getSlideDetailStore } from '@/common/stores/slide-detail';
import { isNil } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { SlideDetailAdapterParams } from '@/common/types/slideDetailAdapter.types';
import { useDetailAdapterByCategory } from '@/common/composables/useOpenDetailByCategory';
import { isCategory } from '@/ai/utils/types';

/**
 * 객체의 모든 값을 평탄화하여 배열로 반환
 * @param obj - 평탄화할 객체
 * @returns 평탄화된 값들의 배열
 */
const flattenValues = (obj: unknown = {}): unknown[] => {
  const result: unknown[] = [];

  const traverse = (current: unknown) => {
    if (Array.isArray(current)) {
      current.forEach((item) => traverse(item));
    } else if (current !== null && typeof current === 'object') {
      Object.values(current).forEach((value) => traverse(value));
    } else {
      result.push(current);
    }
  };

  traverse(obj);
  return result;
};

// 동일 타겟 클릭시 타겟 슬라이드 열려야하는 타입
const SAME_TARGET_DETAIL_TYPE_LIST = ['alert'];

/**
 * ### 알람 태그 클릭시 해당 타겟별 상세 슬라이드 연동
 * @returns
 */
export const useAlertTargetDetail = () => {
  const { t } = useInternational();
  const { showErrorMsg } = useMessage();

  const slideDetailStore = getSlideDetailStore();
  const { isOpened, selectedSlideTabPanel } = storeToRefs(slideDetailStore);

  const { openDetailByCategory } = useDetailAdapterByCategory();

  const setTypeDetail = async ({
    name,
    category,
    targetId,
    timePeriodInfo,
  }: {
    name: string;
    category: string;
    targetId: string;
    timePeriodInfo?: { fromTime: string; toTime: string };
  }) => {
    const { state, type, ...slideRest } = selectedSlideTabPanel.value || {};

    if (
      flattenValues(slideRest).some((value) => !isNil(targetId) && value === targetId) &&
      !SAME_TARGET_DETAIL_TYPE_LIST.some((sameType) => type?.toLowerCase()?.includes(sameType))
    ) {
      return;
    }
    try {
      const stateOptions = { selectedTab: 'information' };

      // 커스텀 타겟 아이디 체크
      const checkCustomTargetId = (_targetId: string) => {
        if (category === 'filesystem' && _targetId.split('|')?.length > 1) {
          const [hostId = '', _fileSystemName = ''] = _targetId.split('|');
          stateOptions.selectedTab = 'disk';
          const fileSystemName = _fileSystemName;
          return { targetIdCheckValue: hostId, name: name.split(';')?.[0] || name, fileSystemName };
        }
        if (category === 'processDetailInfo' && _targetId.split('|')?.length > 1) {
          const [hostId = '', pid = ''] = _targetId.split('|');
          return { targetIdCheckValue: hostId, pid: Number(pid), targetName: name };
        }
        return { targetIdCheckValue: _targetId, targetName: name };
      };

      const {
        targetIdCheckValue,
        targetName,
        fileSystemName,
        pid: processPid,
      } = checkCustomTargetId(targetId);

      const slideDetailAdapterParams: SlideDetailAdapterParams = {
        params: {
          targetId: targetIdCheckValue,
          targetName,
          ...(fileSystemName ? { addition: { fileSystem: fileSystemName } } : {}),
          ...(processPid ? { pid: processPid } : {}),
          ...(timePeriodInfo && {
            fromTime: timePeriodInfo.fromTime,
            toTime: timePeriodInfo.toTime,
          }),
        },
        init: !isOpened.value,
        state: {
          ...state,
          ...stateOptions,
        },
        currentState: {
          keepOpen: true,
        },
      };

      if (isCategory(category)) {
        await openDetailByCategory(category, slideDetailAdapterParams);
      } else {
        console.log('디테일 연계 실패: 카테고리가 없습니다.');
      }
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) showErrorMsg({ msgStr: t('MESSAGE.NOT_FOUND_TARGET') });
      else console.error(`${category} 카테고리 처리 중 오류:`, error);
    }
  };

  return {
    setTypeDetail,
  };
};
